import { isPlatformBrowser } from '@angular/common';
import { HttpEventType } from '@angular/common/http';
import { Component, inject, PLATFORM_ID } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import readXlsxFile from 'read-excel-file';
import { firstValueFrom } from 'rxjs';
import { ModalObjectType } from 'src/app/interfaces/modal-object-types.interface';
import { CompositeArticleImportModel } from 'src/app/models/imports/composite-article-import.model';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { CompositeArticleService } from 'src/app/services/preliminaries/composite-article.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-import-composite-article',
  templateUrl: './import-composite-article.component.html',
  styleUrls: ['./import-composite-article.component.scss'],
  standalone: false
})
export class ImportCompositeArticleComponent {

  private readonly compositeArticleService = inject(CompositeArticleService);
  readonly config = inject(DynamicDialogConfig);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly ref = inject(DynamicDialogRef, { optional: true });
  private readonly platformId = inject(PLATFORM_ID);

  eventFile: any;
  errorCompositeArticleDetail: number = 0;
  loadPreInvoicingModel: LoadPreInvoicingModel = new LoadPreInvoicingModel();
  nameFile: string = '';
  compositeArticleDetailAlerts: Array<PreInvoicingAlertModel> = new Array<PreInvoicingAlertModel>;
  compositeArticleDetailImport: boolean = false;
  visibleTxt: boolean = false;

  attachFileImport() {
    if (isPlatformBrowser(this.platformId)) {
      document.getElementById('fileImport')?.click();
    }
  }

  clearInputFile() {
    this.nameFile = 'Ningún archivo seleccionado.';
    if (this.eventFile) {
      if (isPlatformBrowser(this.platformId)) {
        this.eventFile.target.value = '';
      }
    }
  }

  close() {
    const data: ModalObjectType = {};
    if (this.compositeArticleDetailImport) {
      data.params = { reload: true };
      this.ref?.close(data);
    } else {
      data.params = { reload: false };
      this.ref?.close(data);
    }
  }

  getNameFile(file: any) {
    this.eventFile = file;
    const files = this.eventFile.target.files;
    if (files.length != 0) {
      for (let i = 0; i < files.length; i++) {
        this.nameFile = files[i].name;
      }
    } else {
      this.nameFile = 'Ningún archivo seleccionado.';
    }
  }

  downloadErrorsTxt() {
    if (!this.compositeArticleDetailAlerts || this.compositeArticleDetailAlerts.length === 0) {
      this.toastHandlerService.showToast(
        'error',
        'No hay alertas para exportar'
      );
      return;
    }

    this.compositeArticleService.exportImportErrors(this.compositeArticleDetailAlerts).subscribe({
      next: (blob: Blob) => {
        const file = new Blob([blob], { type: 'text/plain' });
        const url = window.URL.createObjectURL(file);
        const today = new Date();
        const formatDate = today.toISOString().split('T')[0];

        const a = document.createElement('a');
        a.href = url;
        a.download = `ErroresImportacionArticulosCompuestos_${formatDate}.txt`;
        a.click();

        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastHandlerService.showToast(
          'error',
          'Error al exportar el archivo'
        );
      }
    });
  }

  downloadGuideImport() {
    this.compositeArticleService.downloadGuideImport().subscribe(data => {
      switch (data.type) {
        case HttpEventType.Response: {
          const downloadedFile = new Blob([data.body!], { type: data.body!.type });
          const a = document.createElement('a');
          a.setAttribute('style', 'display:none;');
          document.body.appendChild(a);
          a.download = "GuiaImportacionArticulosCompuestos.docx";
          a.href = URL.createObjectURL(downloadedFile);
          a.target = '_blank';
          a.click();
          document.body.removeChild(a);
          break;
        }
      }
    });
  }

  importCompositeArticles() {
    this.compositeArticleDetailAlerts = [];
    this.errorCompositeArticleDetail = 0;
    this.visibleTxt = false;
    if (isPlatformBrowser(this.platformId)) {
      let isOk: boolean = true;
      const input = document.getElementById("fileImport") as any;
      const rowsImports = new Array<CompositeArticleImportModel>();
      let row: CompositeArticleImportModel;
      let counter: number = 1, code: number, alterCode: string, alterCode2: string, name: string,
        observation: string, familyCode: string, groupCode: string, subgroupCode: string,
        active: boolean, articleCode: number, quantity: number;

      if (input.files[0]) {
        readXlsxFile(input.files[0]).then(async (rows) => {
          if (rows.length <= 1) {
            this.toastHandlerService.showToast(
              'error',
              'El archivo ingresado no tiene registros para importar.'
            );
            return;
          }

          rows.splice(0, 1);
          rows.forEach(element => {
            counter = counter + 1;
            let rowHasError = false;

            // A. Código del artículo compuesto:
            const valueCode = element[0];
            if (valueCode == null || valueCode === "" || isNaN(Number(valueCode))) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna A. El código del artículo compuesto no es válido.`;
              this.compositeArticleDetailAlerts.push(alert);
              rowHasError = true;
            }
            code = Number(valueCode);

            // B. Código Alterno:
            alterCode = element[1] as string;
            if (alterCode != null && alterCode.length > 20) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna B. El código alterno del artículo compuesto supera el tamaño máximo de caracteres (20).`;
              this.compositeArticleDetailAlerts.push(alert);
              rowHasError = true;
            }

            // C. Código Alterno 2:
            alterCode2 = element[2] as string;
            if (alterCode2 != null && alterCode2.length > 20) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna C. El código alterno 2 del artículo compuesto supera el tamaño máximo de caracteres (20).`;
              this.compositeArticleDetailAlerts.push(alert);
              rowHasError = true;
            }

            // D. Nombre:
            name = this.sanitizeExcelString(element[3]);
            if (!name) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna D. El nombre del artículo compuesto no es válido.`;
              this.compositeArticleDetailAlerts.push(alert);
              rowHasError = true;
            } else if (name.length > 300) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna D. El nombre del artículo compuesto supera el tamaño máximo de caracteres (300).`;
              this.compositeArticleDetailAlerts.push(alert);
              rowHasError = true;
            }

            // E. Observación:
            observation = this.sanitizeExcelString(element[4]);
            if (observation && observation.length > 1000) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna E. La observación del artículo compuesto supera el tamaño máximo de caracteres (1000).`;
              this.compositeArticleDetailAlerts.push(alert);
              rowHasError = true;
            }

            // F. Código Familia:
            familyCode = element[5] as string;
            if (familyCode == null) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna F. El código de la familia del artículo compuesto no es válido.`;
              this.compositeArticleDetailAlerts.push(alert);
              rowHasError = true;
            }

            // G. Código Grupo:
            groupCode = element[6] as string;
            if (groupCode == null) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna G. El código del grupo del artículo compuesto no es válido.`;
              this.compositeArticleDetailAlerts.push(alert);
              rowHasError = true;
            }

            // H. Código Subgrupo:
            subgroupCode = element[7] as string;
            if (subgroupCode == null) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna H. El código del subgrupo del artículo compuesto no es válido.`;
              this.compositeArticleDetailAlerts.push(alert);
              rowHasError = true;
            }

            // I. Activo:
            if (element[8] as string != "A" && element[8] as string != "I") {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna I. El estado del artículo compuesto debe ser valores como: A / I.`;
              this.compositeArticleDetailAlerts.push(alert);
              rowHasError = true;
            }
            active = element[8] as string === "A";

            // J. Código del artículo Despiece:
            const valueArticleCode = element[9];
            if (valueArticleCode == null || valueArticleCode === "" || isNaN(Number(valueArticleCode))) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna J. El código del artículo Despiece no es válido.`;
              this.compositeArticleDetailAlerts.push(alert);
              rowHasError = true;
            }
            articleCode = Number(valueArticleCode);

            // K. Cantidad:
            const valueQuantity = element[10];
            if (valueQuantity == null || valueQuantity === "" || isNaN(Number(valueQuantity))) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna K. La cantidad no es válida.`;
              this.compositeArticleDetailAlerts.push(alert);
              rowHasError = true;
            }
            quantity = Number(valueQuantity);

            if (!rowHasError) {
              row = new CompositeArticleImportModel(
                counter, code, alterCode, alterCode2, name, observation,
                familyCode, groupCode, subgroupCode, active, articleCode, quantity
              );
              rowsImports.push(row);
            } else {
              isOk = false;
            }
          });

          if (isOk && rowsImports.length > 0) {
            try {
              const response = await firstValueFrom(
                this.compositeArticleService.importCompositesArticles(rowsImports)
              );
              this.loadPreInvoicingModel = response;
              this.compositeArticleDetailAlerts = response?.alerts ?? [];
              this.errorCompositeArticleDetail = response?.preInvoicingStateId;

              if (this.errorCompositeArticleDetail === 1) {
                this.visibleTxt = true;
                this.toastHandlerService.showToast(
                  'error',
                  'La importación terminó con errores, puede exportarlos si lo desea.',
                  'ERROR'
                );
              } else if (this.compositeArticleDetailAlerts.length > 0) {
                this.visibleTxt = true;
                this.toastHandlerService.showToast(
                  'error',
                  'La importación terminó con alertas, puede exportarlas si lo desea'
                );
              } else {
                this.visibleTxt = false;
                this.compositeArticleDetailImport = true;
                this.toastHandlerService.showToast(
                  'success',
                  `La importación terminó exitosamente.`
                );
                this.close();
              }
              this.clearInputFile();
            } catch (error) {
              this.nameFile = 'Ningún archivo seleccionado.';
              console.error('Error al importar artículos compuestos.', error);
            }
          } else {
            this.errorCompositeArticleDetail = 1;
            this.visibleTxt = true;
            this.toastHandlerService.showToast(
              'error',
              'La importación terminó con errores, puede exportarlos si lo desea'
            );
          }
        });
      } else {
        this.toastHandlerService.showToast(
          'error',
          'No ha seleccionado ningún archivo.'
        );
      }
    }
  }

  sanitizeExcelString(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value)
      .replace(/_x([0-9a-fA-F]{4})_/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .trim();
  }
}
